import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

/**
 * SendLumensModal - Lumen transaction modal component
 * Handles sending lumens to post creators
 */
const SendLumensModal = ({
	isOpen,
	onClose,
	selectedPostId,
	lumensToSend: initialLumensToSend = 5,
	onSendLumens,
	userStore,
	newsfeed,
}) => {
	const [lumensToSend, setLumensToSend] = useState(initialLumensToSend);

	if (!isOpen) {
		return null;
	}

	const handleSend = async () => {
		// Get the post to find the recipient MemberID
		const post = newsfeed.posts.find(
			(p) =>
				p.GlowID === selectedPostId ||
				p.PrideID === selectedPostId ||
				p.GratitudeID === selectedPostId
		);
		if (!post) {
			toast.error("Post not found");
			return;
		}

		// Prevent sending lumens to yourself
		if (post.MemberID === userStore.user?.MemberID) {
			toast.error("You cannot send lumens to yourself!");
			return;
		}

		// Use the Zustand store handler for optimistic UI updates
		const result = await onSendLumens(
			selectedPostId,
			lumensToSend,
			post.MemberID
		);

		if (result.success) {
			onClose();
			setLumensToSend(5); // Reset to default
		}
	};

	const handleClose = () => {
		onClose();
		setLumensToSend(5); // Reset to default
	};

	const currentBalance = userStore.user?.CurrentLumens || 0;
	const hasInsufficientLumens =
		lumensToSend > currentBalance && lumensToSend > 0;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 dark:bg-black/50">
			<div className="my-auto w-full max-w-md space-y-4 rounded-lg bg-white p-4 shadow-xl dark:bg-gray-800 sm:p-6">
				<div className="flex items-center justify-between">
					<h2 className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 bg-clip-text text-2xl font-black tracking-tight text-transparent drop-shadow-[0_2px_8px_rgba(251,191,36,0.4)] sm:text-3xl">
						Send Lumens
					</h2>
					<button
						onClick={handleClose}
						className="rounded bg-gray-200 p-1 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
					>
						<X size={20} />
					</button>
				</div>

				{/* Current Balance Display */}
				<div
					className={`flex items-center justify-center gap-2 rounded-lg p-3 ${
						hasInsufficientLumens
							? "bg-gradient-to-r from-red-500 to-red-600 dark:from-red-600 dark:to-red-700"
							: "bg-gradient-to-r from-yellow-400 to-amber-500 dark:from-yellow-500 dark:to-amber-600"
					}`}
				>
					<span className="relative inline-block h-5 w-5">
						<span className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-amber-600" />
						<span className="absolute inset-0.5 rounded-full bg-gradient-to-br from-yellow-100 via-orange-300 to-yellow-200" />
						<span className="absolute left-1 top-1 h-2 w-2 rounded-full bg-white/90 blur-[1px]" />
					</span>
					<span className="text-sm font-bold text-white">
						Your Balance: {currentBalance.toLocaleString()}
					</span>
				</div>

				{/* Insufficient Lumens Error Message */}
				{hasInsufficientLumens ? (
					<div className="rounded-lg bg-red-100 p-3 text-center dark:bg-red-900/30">
						<p className="text-sm font-semibold text-red-600 dark:text-red-400">
							Insufficient lumens
						</p>
					</div>
				) : null}

				<div className="space-y-4">
					<div className="space-y-2">
						<label className="text-sm font-medium text-gray-900 dark:text-white">
							Amount to Send
						</label>
						<input
							type="number"
							min="1"
							value={lumensToSend === 0 ? "" : lumensToSend}
							onChange={(e) => {
								const { value } = e.target;
								if (value === "") {
									setLumensToSend(0);
								} else {
									setLumensToSend(parseInt(value) || 0);
								}
							}}
							placeholder="0"
							className={`w-full rounded-lg border p-3 ${
								hasInsufficientLumens
									? "border-red-500 bg-red-50 text-red-900 placeholder:text-red-400 dark:border-red-500 dark:bg-red-900/20 dark:text-red-200"
									: "border-gray-200 bg-gray-100 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white"
							}`}
						/>
					</div>

					<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
						{[5, 10, 25, 50].map((amount) => (
							<button
								key={amount}
								onClick={() => setLumensToSend(amount)}
								className={`flex items-center justify-center gap-1 rounded-lg p-2 text-sm transition-colors ${
									lumensToSend === amount
										? "bg-yellow-500 text-black"
										: "bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
								}`}
							>
								{amount}
								<span className="relative inline-block h-4 w-4">
									<span className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-amber-600" />
									<span className="absolute inset-0.5 rounded-full bg-gradient-to-br from-yellow-100 via-orange-300 to-yellow-200" />
									<span className="absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-white/90 blur-[0.5px]" />
								</span>
							</button>
						))}
					</div>
				</div>

				<div className="flex gap-3">
					<button
						onClick={handleClose}
						className="flex-1 rounded-lg bg-gray-200 p-3 text-gray-900 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
					>
						Cancel
					</button>
					<button
						onClick={handleSend}
						disabled={lumensToSend < 1}
						className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-3 text-white transition-colors hover:from-purple-700 hover:to-pink-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Send {lumensToSend}
						<span className="relative inline-block h-4 w-4">
							<span className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-amber-600" />
							<span className="absolute inset-0.5 rounded-full bg-gradient-to-br from-yellow-100 via-orange-300 to-yellow-200" />
							<span className="absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-white/90 blur-[0.5px]" />
						</span>
					</button>
				</div>
			</div>
		</div>
	);
};

export default SendLumensModal;
